import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { skipToken, useQuery } from '@tanstack/react-query';
import { Navigate, useSearchParams } from 'react-router';

import { NotFoundPage } from '@/app/shell/NotFoundPage';
import { buildGetIncidentsRequest } from '@/features/incident-archive/filters';
import { getIncidents } from '@/features/incident-archive/incidentsApi';
import { incidentArchiveUrl, openIncidentSearchUrl, resolveOpenIncidentTarget } from './openIncidentRedirect';

export function OpenIncidentPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const target = useMemo(() => resolveOpenIncidentTarget(searchParams), [searchParams]);
  const request = useMemo(
    () => (target.kind === 'search' ? buildGetIncidentsRequest(target.filters) : null),
    [target],
  );

  const { data: matches, isError } = useQuery({
    queryKey: ['open-incident', request],
    queryFn: request === null ? skipToken : () => getIncidents(request),
  });

  if (target.kind === 'notFound') {
    return <NotFoundPage />;
  }

  if (target.kind === 'redirect') {
    return <Navigate to={target.url} replace />;
  }

  if (isError) {
    return <Navigate to={incidentArchiveUrl(target.filters)} replace />;
  }

  if (matches === undefined) {
    return (
      <div className="d-flex align-items-center justify-content-center gap-2 p-5" role="status">
        <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
        <span>{t('OpenIncident_SearchingIncidents')}</span>
      </div>
    );
  }

  return <Navigate to={openIncidentSearchUrl(matches, target.filters)} replace />;
}
